import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import ConfirmModal from './ConfirmModal'; 
import GameManager from '../components/GameManager';

const AdminPanel = () => {
    const navigate = useNavigate();
    
    // --- STATE ---
    const [activeTab, setActiveTab] = useState('users'); 
    const [stats, setStats] = useState({ totalUsers: 0, totalTournaments: 0, activeTournaments: 0 });
    const [users, setUsers] = useState([]);
    const [tournaments, setTournaments] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // --- MODAL STATE ---
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: '',
        message: '',
        confirmText: '',
        confirmColor: '',
        onConfirm: () => {}
    });

    const openModal = (title, message, confirmText, confirmColor, onConfirm) => {
        setModalConfig({ isOpen: true, title, message, confirmText, confirmColor, onConfirm });
    };

    const closeModal = () => {
        setModalConfig({ ...modalConfig, isOpen: false });
    };

    // --- FETCH DATA ---
    useEffect(() => {
        fetchAdminData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAdminData = async () => {
        setIsLoading(true);
        try {
            const [statsRes, usersRes, tourneysRes] = await Promise.all([
                api.get('/admin/stats'),
                api.get('/admin/users'),
                api.get('/tournaments/admin/all')
            ]);
            setStats(statsRes.data);
            setUsers(usersRes.data);
            setTournaments(tourneysRes.data);
            setError(''); // Clear error on success
        } catch (err) {
            setError('Failed to load admin data. Ensure backend is running.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- IDENTITY ---
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const currentAdminId = (storedUser.id || storedUser._id)?.toString();

    // --- USER ACTIONS ---
    const handleRoleChange = (id, username, targetRole) => {
        openModal(
            "Change User Role",
            `Are you sure you want to change ${username}'s role to ${targetRole.toUpperCase()}?`,
            `Set to ${targetRole.toUpperCase()}`,
            targetRole === 'admin' ? 'bg-purple-600 hover:bg-purple-700' : targetRole === 'master' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700',
            async () => {
                try {
                    await api.put(`/admin/users/${id}/role`, { role: targetRole });
                    fetchAdminData();
                } catch (err) { alert(err.response?.data?.error || 'Error changing role'); }
            }
        );
    };

    const toggleUserVerify = async (id, currentStatus) => {
        try {
            await api.put(`/admin/users/${id}/verify`);
            fetchAdminData();
        } catch (err) { alert('Error updating verification'); }
    };

    const resetUserPassword = (id, email) => {
        openModal(
            "Force Password Reset",
            `Send a mandatory password reset email to ${email}? They will need to set a new password to log in.`,
            "Send Email",
            "bg-blue-600 hover:bg-blue-700",
            async () => {
                try {
                    await api.post(`/admin/users/${id}/reset-password`);
                    alert(`Reset email successfully sent to ${email}`);
                } catch (err) { alert('Error sending reset email'); }
            }
        );
    };

    const deleteUser = (id, username) => {
        openModal(
            "Delete User Account",
            `CRITICAL WARNING: Are you sure you want to completely delete ${username} and ALL their tournaments? This action cannot be undone.`,
            "Delete User",
            "bg-red-600 hover:bg-red-700",
            async () => {
                try {
                    await api.delete(`/admin/users/${id}`);
                    fetchAdminData();
                } catch (err) { alert(err.response?.data?.error || 'Error deleting user'); }
            }
        );
    };

    // --- TOURNAMENT ACTIONS ---
    const deleteTournament = (id, name) => {
        openModal(
            "Delete Tournament",
            `Are you sure you want to permanently delete the tournament "${name}"? All match history will be lost.`,
            "Delete Tournament",
            "bg-red-600 hover:bg-red-700",
            async () => {
                try {
                    await api.delete(`/tournaments/${id}`);
                    fetchAdminData();
                } catch (err) { alert('Error deleting tournament'); }
            }
        );
    };

    // --- FILTERING LOGIC ---
    const safeQuery = (searchQuery || '').toLowerCase();
    const filteredUsers = users.filter(u => (u.username || '').toLowerCase().includes(safeQuery) || (u.email || '').toLowerCase().includes(safeQuery));
    
    // PHASE 5 FIX: We now use masterId instead of owner!
    const filteredTournaments = tournaments.filter(t => 
        (t.name || '').toLowerCase().includes(safeQuery) || 
        (t.masterId?.username || '').toLowerCase().includes(safeQuery)
    );

    if (isLoading) return <div className="min-h-screen flex items-center justify-center text-xl font-bold text-gray-500">Loading Admin Matrix...</div>;

    return (
        <div className="min-h-screen bg-gray-50 p-6 md:p-10 font-sans">
            
            <ConfirmModal 
                isOpen={modalConfig.isOpen}
                onClose={closeModal}
                onConfirm={modalConfig.onConfirm}
                title={modalConfig.title}
                message={modalConfig.message}
                confirmText={modalConfig.confirmText}
                confirmColor={modalConfig.confirmColor}
            />

            <div className="max-w-7xl mx-auto space-y-8">
                {/* HEADER & BACK BUTTON */}
                <div className="flex justify-between items-end">
                    <div>
                        <h1 className="text-4xl font-black text-gray-900 tracking-tight">Admin Control Center</h1>
                        <p className="text-gray-500 font-medium mt-1">Manage users, tournaments, and system health.</p>
                    </div>
                    <button onClick={() => navigate('/dashboard')} className="px-5 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold shadow-sm hover:bg-gray-50 transition-all">
                        Return to Dashboard
                    </button>
                </div>

                {error && <div className="bg-red-50 text-red-600 p-4 rounded-xl font-bold border border-red-100">{error}</div>}

                {/* KPI STAT CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-center items-center">
                        <span className="text-gray-500 font-bold text-sm uppercase tracking-wider mb-1">Total Users</span>
                        <span className="text-4xl font-black text-blue-600">{stats.totalUsers}</span>
                    </div>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-center items-center">
                        <span className="text-gray-500 font-bold text-sm uppercase tracking-wider mb-1">Total Tournaments</span>
                        <span className="text-4xl font-black text-purple-600">{stats.totalTournaments}</span>
                    </div>
                    <div className="bg-white p-6 rounded-3xl shadow-sm border border-gray-100 flex flex-col justify-center items-center">
                        <span className="text-gray-500 font-bold text-sm uppercase tracking-wider mb-1">Active Tournaments</span>
                        <span className="text-4xl font-black text-green-600">{stats.activeTournaments}</span>
                    </div>
                </div>
               
                {/* TABS & SEARCH */}
                <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex flex-col sm:flex-row justify-between items-center p-4 border-b border-gray-100 bg-gray-50/50">
                        <div className="flex space-x-2 w-full sm:w-auto mb-4 sm:mb-0">
                            <button onClick={() => setActiveTab('users')} className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-black text-sm transition-all ${activeTab === 'users' ? 'bg-blue-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}>
                                Users Matrix
                            </button>
                            <button onClick={() => setActiveTab('tournaments')} className={`flex-1 sm:flex-none px-6 py-3 rounded-xl font-black text-sm transition-all ${activeTab === 'tournaments' ? 'bg-purple-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}>
                                Tournaments
                            </button>
                        </div>
                        <input type="text" placeholder={`Search ${activeTab}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full sm:w-72 px-4 py-3 rounded-xl bg-white border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none font-medium"/>
                    </div>

                    {/* TAB CONTENT: USERS */}
                    {activeTab === 'users' && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500">
                                        <th className="p-4 font-black">User Info</th>
                                        <th className="p-4 font-black">Role</th>
                                        <th className="p-4 font-black">Status</th>
                                        <th className="p-4 font-black text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm">
                                    {filteredUsers.map(user => {
                                        const isSelf = currentAdminId && user._id === currentAdminId;
                                        const role = user.role || 'player';
                                        return (
                                            <tr key={user._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                                <td className="p-4">
                                                    <div className="font-bold text-gray-900">{user.username}</div>
                                                    <div className="text-gray-500">{user.email}</div>
                                                </td>
                                                <td className="p-4">
                                                    {isSelf ? (
                                                        <span className="px-3 py-1.5 rounded-xl text-xs font-black bg-purple-100 text-purple-700 border border-purple-200 inline-flex items-center gap-1">
                                                            ADMIN <span className="text-[10px] text-purple-500 font-bold">(YOU)</span>
                                                        </span>
                                                    ) : (
                                                        <select
                                                            value={role}
                                                            onChange={(e) => handleRoleChange(user._id, user.username, e.target.value)}
                                                            className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-wider cursor-pointer outline-none border transition-all ${
                                                                role === 'admin'
                                                                    ? 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100 focus:ring-2 focus:ring-purple-400'
                                                                    : role === 'master'
                                                                    ? 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 focus:ring-2 focus:ring-amber-400'
                                                                    : 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 focus:ring-2 focus:ring-blue-400'
                                                            }`}
                                                        >
                                                            <option value="player">Player</option>
                                                            <option value="master">Master</option>
                                                            <option value="admin">Admin</option>
                                                        </select>
                                                    )}
                                                </td>
                                                <td className="p-4">
                                                    <button onClick={() => toggleUserVerify(user._id, user.isVerified)} className={`px-3 py-1 rounded-full text-xs font-bold transition-transform active:scale-95 ${user.isVerified ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-orange-100 text-orange-700 hover:bg-orange-200'}`}>
                                                        {user.isVerified ? 'Verified' : 'Unverified'}
                                                    </button>
                                                </td>
                                                <td className="p-4 text-right space-x-2">
                                                    <button onClick={() => resetUserPassword(user._id, user.email)} className="text-blue-600 hover:text-blue-800 font-bold text-xs bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors">Reset Pass</button>
                                                    {!isSelf && (
                                                        <button 
                                                            onClick={() => {
                                                                const nextRole = role === 'player' ? 'master' : role === 'master' ? 'admin' : 'player';
                                                                handleRoleChange(user._id, user.username, nextRole);
                                                            }}
                                                            className="text-purple-600 hover:text-purple-800 font-bold text-xs bg-purple-50 hover:bg-purple-100 px-3 py-2 rounded-lg transition-colors"
                                                            title="Cycle to next role"
                                                        >
                                                            Cycle Role
                                                        </button>
                                                    )}
                                                    <button onClick={() => deleteUser(user._id, user.username)} className="text-red-600 hover:text-red-800 font-bold text-xs bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-colors">Delete</button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    {filteredUsers.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-500 font-medium">No users found matching "{searchQuery}"</td></tr>}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* TAB CONTENT: TOURNAMENTS */}
                    {activeTab === 'tournaments' && (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-100 text-xs uppercase tracking-wider text-gray-500">
                                        <th className="p-4 font-black">Tournament Info</th>
                                        <th className="p-4 font-black">Master/Owner</th>
                                        <th className="p-4 font-black">Status</th>
                                        <th className="p-4 font-black text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="text-sm">
                                    {filteredTournaments.map(tourney => (
                                        <tr key={tourney._id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4"><div className="font-bold text-gray-900 text-base">{tourney.name}</div><div className="text-gray-500 text-xs mt-0.5">{tourney.players?.length || 0} Players • Round {tourney.currentRound}</div></td>
                                            {/* PHASE 5 FIX: Now reads masterId.username */}
                                            <td className="p-4 font-medium text-gray-700">{tourney.masterId?.username || 'Unknown Master'}</td>
                                            <td className="p-4"><span className={`px-3 py-1 rounded-full text-xs font-bold ${tourney.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>{tourney.status.toUpperCase()}</span></td>
                                            <td className="p-4 text-right space-x-2">
                                                {/* FIXED BACKTICKS HERE: */}
                                                <button onClick={() => navigate(`/tournament/${tourney._id}`)} className="text-blue-600 hover:text-blue-800 font-bold text-xs bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-lg transition-colors">View / Edit</button>
                                                <button onClick={() => deleteTournament(tourney._id, tourney.name)} className="text-red-600 hover:text-red-800 font-bold text-xs bg-red-50 hover:bg-red-100 px-3 py-2 rounded-lg transition-colors">Delete</button>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredTournaments.length === 0 && <tr><td colSpan="4" className="p-8 text-center text-gray-500 font-medium">No tournaments found matching "{searchQuery}"</td></tr>}
                                </tbody>
                            </table>
                        </div>
                        
                    )}
                </div>
                 <div className="mt-12">
            <h2 className="text-2xl font-black mb-4">Game Settings</h2>
            
            {/* ⚡ Drop the new module right here! */}
            <GameManager />
            
        </div>
            </div>
        </div>
    );
};

export default AdminPanel;